import { create } from 'zustand'
import { apiRequest } from '../api/client'
import { queryClient } from '../lib/queryClient'
import type { TimeEntry, TimeEntryType } from '../types/api'

export interface TimerDraft {
    description: string
    projectId: number | null
    clientId: number | null
    entryType: TimeEntryType
    isBillable: boolean
}

const DRAFT_KEY = 'optimus.timerDraft'

const emptyDraft: TimerDraft = {
    description: '',
    projectId: null,
    clientId: null,
    entryType: 'WORK',
    isBillable: true,
}

function loadDraft(): TimerDraft {
    try {
        const raw = localStorage.getItem(DRAFT_KEY)
        return raw ? { ...emptyDraft, ...JSON.parse(raw) } : emptyDraft
    } catch {
        return emptyDraft
    }
}

export interface RunningPatch {
    description?: string
    projectId?: number | null
    clientId?: number | null
}

export type ToggleResult = 'started' | 'stopped' | 'switched'

interface TimerState {
    running: TimeEntry | null
    elapsed: number
    draft: TimerDraft
    syncing: boolean
    loadRunning: (workspaceId: number) => Promise<void>
    start: (workspaceId: number, entryType?: TimeEntryType) => Promise<void>
    stop: () => Promise<void>
    toggle: (
        workspaceId: number,
        entryType: TimeEntryType,
    ) => Promise<ToggleResult>
    updateRunning: (patch: RunningPatch) => Promise<void>
    setDraft: (patch: Partial<TimerDraft>) => void
}

let ticker: ReturnType<typeof setInterval> | null = null

function tickFromEntry(entry: TimeEntry): number {
    return Math.max(
        0,
        Math.floor((Date.now() - new Date(entry.startTime).getTime()) / 1000),
    )
}

function startTicker(get: () => TimerState, set: (s: Partial<TimerState>) => void) {
    stopTicker()
    ticker = setInterval(() => {
        const entry = get().running
        if (entry) set({ elapsed: tickFromEntry(entry) })
    }, 1000)
}

function stopTicker() {
    if (ticker) clearInterval(ticker)
    ticker = null
}

export const useTimerStore = create<TimerState>()((set, get) => ({
    running: null,
    elapsed: 0,
    draft: loadDraft(),
    syncing: false,

    loadRunning: async workspaceId => {
        const entry = await apiRequest<TimeEntry | null>(
            `/time-entries/running?workspaceId=${workspaceId}`,
        )
        if (entry) {
            set({ running: entry, elapsed: tickFromEntry(entry) })
            startTicker(get, set)
        } else {
            stopTicker()
            set({ running: null, elapsed: 0 })
        }
    },

    start: async (workspaceId, entryType) => {
        const draft = { ...get().draft }
        if (entryType) draft.entryType = entryType
        set({ syncing: true })
        try {
            const entry = await apiRequest<TimeEntry>('/time-entries', {
                method: 'POST',
                body: {
                    workspaceId,
                    description: draft.description,
                    projectId: draft.projectId ?? undefined,
                    clientId: draft.clientId ?? undefined,
                    entryType: draft.entryType,
                    // Pausen und Abwesenheiten sind per Definition nicht abrechenbar
                    isBillable:
                        draft.entryType === 'BREAK' ||
                        draft.entryType === 'SICK' ||
                        draft.entryType === 'VACATION' ||
                        draft.entryType === 'TIME_OFF' ||
                        draft.entryType === 'PAID_ABSENCE'
                            ? false
                            : draft.isBillable,
                    startTime: new Date().toISOString(),
                },
            })
            set({ running: entry, elapsed: 0, draft })
            localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
            startTicker(get, set)
            void queryClient.invalidateQueries({ queryKey: ['entries'] })
            void queryClient.invalidateQueries({ queryKey: ['summary'] })
            void queryClient.invalidateQueries({ queryKey: ['running'] })
            void queryClient.invalidateQueries({ queryKey: ['workingTimeMonthly'] })
        } finally {
            set({ syncing: false })
        }
    },

    stop: async () => {
        const entry = get().running
        if (!entry) return
        set({ syncing: true })
        try {
            stopTicker()
            set({ running: null, elapsed: 0 })
            await apiRequest<TimeEntry>(`/time-entries/${entry.id}/stop`, {
                method: 'PATCH',
            })
            void queryClient.invalidateQueries({ queryKey: ['entries'] })
            void queryClient.invalidateQueries({ queryKey: ['summary'] })
            void queryClient.invalidateQueries({ queryKey: ['running'] })
            void queryClient.invalidateQueries({ queryKey: ['workingTimeMonthly'] })
        } finally {
            set({ syncing: false })
        }
    },

    toggle: async (workspaceId, entryType) => {
        const { running } = get()
        if (!running) {
            await get().start(workspaceId, entryType)
            return 'started'
        }
        if (running.entryType === entryType) {
            await get().stop()
            return 'stopped'
        }
        // Backend liefert bei laufendem Timer auf POST 409 — erst stoppen.
        await get().stop()
        await get().start(workspaceId, entryType)
        return 'switched'
    },

    updateRunning: async patch => {
        const entry = get().running
        if (!entry) return
        const updated = await apiRequest<TimeEntry>(
            `/time-entries/${entry.id}`,
            { method: 'PATCH', body: patch },
        )
        // Nur übernehmen, wenn nicht zwischenzeitlich gestoppt/gewechselt wurde
        if (get().running?.id === entry.id) set({ running: updated })
    },

    setDraft: patch => {
        const draft = { ...get().draft, ...patch }
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
        set({ draft })
    },
}))
